// ============================================================
// CLUBHUB – Vorkommen: aus Terminen werden Tage
// ============================================================
// Ein Termin mit RRULE steht EINMAL in der Datenbank und an vielen
// Tagen im Kalender. Diese Datei macht aus Terminen die Vorkommen
// eines Zeitraums: Serie entfalten, ausgelassene Tage (`exdates`)
// weglassen, Ausnahmen anwenden (abgesagt, verschoben) und jedem
// Vorkommen einen Schluessel fuer die Adresse geben.
//
// SCHLUESSEL: Einzeltermin `t-07`, Vorkommen einer Serie
// `t-01~2026-10-27` (Tag laut Serie). Ein verschobenes Vorkommen
// behaelt den Schluessel seines urspruenglichen Tages — so bleiben
// Zusagen daran haengen, wie `EventResponse.occurrence` es will.
// ============================================================
import type { TerminView, VorkommenView, ZusageZaehler } from "./views";
import { addDays, tagVon, type DayKey } from "./tag";
import { serieEntfalten } from "./wiederholung";

const TRENNER = "~";

export function vorkommenSchluessel(eventId: string, occurrence: string | null): string {
  return occurrence ? `${eventId}${TRENNER}${tagVon(occurrence)}` : eventId;
}

/** `t-01~2026-10-27` → { eventId: "t-01", tag: "2026-10-27" } */
export function schluesselLesen(key: string): { eventId: string; tag: DayKey | null } {
  const roh = decodeURIComponent(key);
  const i = roh.indexOf(TRENNER);
  if (i === -1) return { eventId: roh, tag: null };
  return { eventId: roh.slice(0, i), tag: roh.slice(i + 1) };
}

function dauerMinuten(t: TerminView): number | null {
  if (!t.endsAt) return null;
  return Math.round((Date.parse(`${t.endsAt}Z`) - Date.parse(`${t.startsAt}Z`)) / 60000);
}

function endeNach(start: string, minuten: number | null): string | null {
  if (minuten === null) return null;
  return new Date(Date.parse(`${start}Z`) + minuten * 60000).toISOString().slice(0, 19);
}

/** Der letzte Tag, den ein Vorkommen beruehrt (mehrtaegige Termine). */
export function letzterTag(v: { startsAt: string; endsAt: string | null; allDay: boolean }): DayKey {
  if (!v.endsAt) return tagVon(v.startsAt);
  const ende = tagVon(v.endsAt);
  // Ein zeitlicher Termin, der um 00:30 endet, gehoert noch zum Vortag.
  if (!v.allDay && v.endsAt.slice(11, 16) <= "06:00" && ende > tagVon(v.startsAt)) return addDays(ende, -1);
  return ende < tagVon(v.startsAt) ? tagVon(v.startsAt) : ende;
}

type Zusatz = {
  zaehler?: (termin: TerminView, occurrence: string | null) => ZusageZaehler | null;
};

function basis(t: TerminView): Omit<VorkommenView, "key" | "occurrence" | "startsAt" | "endsAt" | "status" | "reason" | "movedFrom" | "zaehler"> {
  const frei = t.shifts.length
    ? t.shifts.reduce((s, x) => s + Math.max(0, x.needed - x.helfer.length), 0)
    : null;
  return {
    eventId: t.id,
    title: t.title,
    location: t.location,
    allDay: t.allDay,
    categoryId: t.categoryId,
    groupIds: t.groupIds,
    fuerAlle: t.groupIds.length === 0 && t.contactIds.length === 0,
    einzelne: t.contactIds.length,
    visibility: t.visibility,
    isSeries: Boolean(t.rrule),
    rsvpEnabled: t.rsvpEnabled,
    maxParticipants: t.maxParticipants,
    freiePlaetze: frei,
  };
}

/** Alle Vorkommen eines Termins, die den Zeitraum beruehren. */
export function vorkommenVon(t: TerminView, von: DayKey, bis: DayKey, zusatz: Zusatz = {}): VorkommenView[] {
  const b = basis(t);
  if (!t.rrule) {
    const v: VorkommenView = {
      ...b,
      key: t.id,
      occurrence: null,
      startsAt: t.startsAt,
      endsAt: t.endsAt,
      status: t.cancelledAt ? "abgesagt" : "geplant",
      reason: t.cancelReason,
      movedFrom: null,
      zaehler: null,
    };
    if (letzterTag(v) < von || tagVon(v.startsAt) > bis) return [];
    v.zaehler = zusatz.zaehler?.(t, null) ?? null;
    return [v];
  }

  const dauer = dauerMinuten(t);
  const aus = new Set(t.exdates.map(tagVon));
  // Verschobene koennen aus dem Zeitraum heraus- oder hineinwandern:
  // eine Woche Luft auf beiden Seiten.
  const roh = serieEntfalten(t.startsAt, t.rrule, addDays(von, -7), addDays(bis, 7));
  const ergebnis: VorkommenView[] = [];
  for (const occ of roh) {
    if (aus.has(tagVon(occ))) continue;
    const ausnahme = t.ausnahmen.find((a) => tagVon(a.occurrence) === tagVon(occ));
    const startsAt = ausnahme?.startsAt ?? occ;
    const endsAt = ausnahme?.startsAt ? ausnahme.endsAt : endeNach(occ, dauer);
    const v: VorkommenView = {
      ...b,
      key: vorkommenSchluessel(t.id, occ),
      occurrence: occ,
      startsAt,
      endsAt,
      status: t.cancelledAt || ausnahme?.cancelled ? "abgesagt" : ausnahme?.startsAt ? "verschoben" : "geplant",
      reason: ausnahme?.reason ?? (t.cancelledAt ? t.cancelReason : null),
      movedFrom: ausnahme?.startsAt ? occ : null,
      zaehler: null,
    };
    if (letzterTag(v) < von || tagVon(v.startsAt) > bis) continue;
    v.zaehler = zusatz.zaehler?.(t, occ) ?? null;
    ergebnis.push(v);
  }
  return ergebnis;
}

/** Vorkommen vieler Termine, nach Beginn sortiert. */
export function vorkommenImZeitraum(termine: TerminView[], von: DayKey, bis: DayKey, zusatz: Zusatz = {}): VorkommenView[] {
  return termine
    .flatMap((t) => vorkommenVon(t, von, bis, zusatz))
    .sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.startsAt.localeCompare(b.startsAt) || a.title.localeCompare(b.title));
}

/** Das eine Vorkommen zum Schluessel — oder `null`, wenn es den Tag nicht gibt. */
export function vorkommenZu(t: TerminView, tag: DayKey | null, zusatz: Zusatz = {}): VorkommenView | null {
  if (!t.rrule) return vorkommenVon(t, tagVon(t.startsAt), letzterTag(t), zusatz)[0] ?? null;
  if (!tag) return null;
  // Weiter gefasst: ein verschobenes Vorkommen liegt womoeglich an einem anderen Tag.
  return vorkommenVon(t, addDays(tag, -7), addDays(tag, 7), zusatz).find((v) => v.occurrence && tagVon(v.occurrence) === tag) ?? null;
}
